import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import type { PortfolioHistoryPoint } from '@/types'
import { formatCurrency } from '@/lib/utils'

interface Props {
  data: PortfolioHistoryPoint[]
  height?: number
}

const CustomTooltip = ({ active, payload, label }: {
  active?: boolean; payload?: Array<{ value: number }>; label?: string
}) => {
  if (!active || !payload?.length) return null

  return (
    <div className="bg-[#1e1f23] border border-white/10 rounded-lg px-3 py-2 shadow-xl">
      <div className="text-xs text-white/40 mb-1">{label}</div>
      <div className="text-sm font-semibold font-mono text-white">
        {formatCurrency(payload[0].value)}
      </div>
    </div>
  )
}

export default function PortfolioChart({ data, height = 220 }: Props) {
  if (!data.length) {
    return (
      <div
        className="flex items-center justify-center text-white/30 text-sm"
        style={{ height }}
      >
        No history yet — make your first trade to see your chart
      </div>
    )
  }

  const firstValue = data[0]?.total_value ?? 100000
  const lastValue = data[data.length - 1]?.total_value ?? 100000
  const isPositive = lastValue >= firstValue
  const strokeColor = isPositive ? '#00d4aa' : '#ff4d6d'
  const fillId = isPositive ? 'gainGradient' : 'lossGradient'
  const fillColor = isPositive ? '#00d4aa' : '#ff4d6d'

  const minValue = Math.min(...data.map((d) => d.total_value)) * 0.995
  const maxValue = Math.max(...data.map((d) => d.total_value)) * 1.005

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="gainGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00d4aa" stopOpacity={0.2} />
            <stop offset="100%" stopColor="#00d4aa" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="lossGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ff4d6d" stopOpacity={0.2} />
            <stop offset="100%" stopColor="#ff4d6d" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
        <XAxis
          dataKey="date"
          tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(val: string) => {
            const d = new Date(val)
            return `${d.getMonth() + 1}/${d.getDate()}`
          }}
          interval="preserveStartEnd"
        />
        <YAxis
          domain={[minValue, maxValue]}
          tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(val: number) => `$${(val / 1000).toFixed(0)}k`}
          width={52}
        />
        <Tooltip content={<CustomTooltip />} />
        <Area
          type="monotone"
          dataKey="total_value"
          stroke={strokeColor}
          strokeWidth={1.5}
          fill={`url(#${fillId})`}
          fillOpacity={1}
          dot={false}
          activeDot={{ r: 4, fill: strokeColor, strokeWidth: 0 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}
